import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { rrulestr } from 'rrule';
import { Event } from '@/types/EventInterface';
import { Heading } from '@/components';
import { ClockIcon } from 'lucide-react';

interface ScheduleProps {
  eventDates: Event['eventDates'];
}

const Schedule: React.FC<ScheduleProps> = ({ eventDates }) => {
  if (!eventDates?.length) return null;

  const dateMonth = (date: string) => {
    return formatInTimeZone(new Date(date), 'America/New_York', 'MMM');
  };

  const dateDay = (date: string) => {
    return formatInTimeZone(new Date(date), 'America/New_York', 'd');
  };

  const dateYear = (date: string) => {
    return formatInTimeZone(new Date(date), 'America/New_York', 'yyyy');
  };

  const DateBox = ({ date }: { date: string }) => {
    return (
      <div className='flex flex-col items-center gap-y-0 rounded-lg border border-gray-200 px-3 py-1'>
        <div className='text-sm'>{dateMonth(date)}</div>
        <div className='text-normal font-normal lowercase leading-5 sm:text-lg'>
          {dateDay(date)}
        </div>
        <div className='text-xs'>{dateYear(date)}</div>
      </div>
    );
  };

  return (
    <div className='mx-auto flex max-w-screen-xl flex-col px-5 py-8 pt-0 md:p-[50px] md:pt-0'>
      <div className='pb-6 last:pb-0 md:pb-9'>
        <Heading title='schedule' />
        {eventDates.map((dateRange, index) => {
          const { startDate, endDate, rrule } = dateRange.frequency;

          const formattedTimeStart = formatInTimeZone(
            new Date(startDate),
            'America/New_York',
            'h:mm a',
          );

          const formattedTimeEnd = formatInTimeZone(
            new Date(endDate),
            'America/New_York',
            'h:mm a',
          );

          const readableRecurrence = rrule
            ? rrulestr(rrule).toText().charAt(0).toUpperCase() + rrulestr(rrule).toText().slice(1)
            : 'One-time event';

          return (
            <div
              key={index}
              className='flex items-center gap-x-5 rounded-lg bg-white p-4'
            >
              <div className='flex flex-col items-center gap-y-2'>
                <DateBox date={startDate} />
                <div className='h-[20px] w-[1px] bg-gray-200' />
                <DateBox date={endDate} />
              </div>
              <div className='flex flex-col gap-y-2'>
                <div className='text-2xl leading-5'>{readableRecurrence}</div>
                <div className='text-normal flex items-center gap-x-2'>
                  <ClockIcon className='h-4 w-4' />
                  <div>
                    {formattedTimeStart} - {formattedTimeEnd} EST
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Schedule;
